import { CommonModule, CurrencyPipe, DatePipe, DecimalPipe } from "@angular/common";
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import { AppComponent } from "../../app.component";
import { UIComponentsModule } from "../ui-components.module";
import { OrganisationService } from "../../organisation/organisation.service";
import { DEFAULT_CURRENCY } from "../../common/organisation";

export class Column {
    constructor(public name:string, public header:string, public type:string, public tooltip?:string, public colour?:string, public icon?:string) {}
}

// columns that hold something to read, and so can be filtered on
const TEXT_TYPES = ['string', 'button']
const DATE_TYPES = ['date', 'datetime']

// a table of rows with optional filtering, sorting on every column, and rows that can be chosen by click or keyboard
@Component({
    selector: 'app-table',
    templateUrl: './table.html',
    styleUrls: ['./table.css'],
    imports: [CommonModule, MatTableModule, MatButtonModule, MatIconModule, DatePipe, CurrencyPipe, DecimalPipe, MatSortModule, MatTooltipModule, MatFormFieldModule, MatInputModule, UIComponentsModule],
    standalone: true
})
export class TableComponent implements OnChanges {
    @Input() data:any[]
    @Input() columns:Column[]
    @Input() filter:boolean             // whether has a filter
    @Input() rowSelect:boolean          // whether can select row or not
    @Input() height:number

    @Output() rowEvent = new EventEmitter<{row:any, column?:Column}>();
    @Output() dblClickEvent = new EventEmitter<{row:any, column?:Column}>();

    currencyCode:string
    columnNames:string[] = []
    selectedId:any                      // the chosen row's _key, or its position when rows have no key
    filterText = ''

    datasource = new MatTableDataSource<any>();

    // the sort header only exists once the table is drawn, so attach it when it appears
    @ViewChild(MatSort) set sort(sort:MatSort) {
        if (sort) this.datasource.sort = sort
    }

    constructor(public app:AppComponent, organisationService:OrganisationService) {
        this.currencyCode = organisationService.organisation?.currencyCode||DEFAULT_CURRENCY

        // filter on what people can read in the table, not on hidden keys
        this.datasource.filterPredicate = (record, filter) => (this.columns || [])
            .filter(c => TEXT_TYPES.includes(c.type))
            .some(c => String(record[c.name] ?? '').toLowerCase().includes(filter))

        // sort text without regard to case, dates by time, and blanks together at one end
        this.datasource.sortingDataAccessor = (record, name) => {
            const column = this.columns?.find(c => c.name === name)
            const value = record[name]
            if (value === null || value === undefined || value === '') return undefined
            if (column && DATE_TYPES.includes(column.type)) return new Date(value).getTime()
            if (typeof value === 'string') return value.toLowerCase()
            if (typeof value === 'boolean') return value ? 1 : 0
            return value
        }
    }

    ngOnChanges(changes:SimpleChanges) {
        if (changes["columns"]) this.columnNames = this.columns ? this.columns.map(c => c.name) : []
        if (changes["data"] || changes["columns"]) {
            // keep the same data source, so the filter and sort stay as they were when the rows change
            this.datasource.data = this.data ? this.data.map((d, i) => ({...d, key:i})) : []
            if (this.selectedId !== undefined && !this.datasource.data.some(r => this.rowId(r) === this.selectedId)) this.selectedId = undefined
        }
    }

    get total() {
        return this.datasource.data.length
    }

    get shown() {
        return this.datasource.filteredData.length
    }

    rowId(row:any) {
        return row?._key ?? row?.key
    }

    isSelected(row:any) {
        return this.selectedId !== undefined && this.rowId(row) === this.selectedId
    }

    getTableStyle() {
        return `overflow:auto;max-height:${this.height?this.height + 'px;':'400px;'}`
    }

    selectRow(row:any) {
        if (this.rowSelect) {
            this.selectedId = this.rowId(row)
            this.rowEvent.emit({row})
        }
    }

    dblClick(row:any) {
        if (this.rowSelect)
            this.dblClickEvent.emit({row})
    }

    columnClick(row:any, column:Column) {
        this.rowEvent.emit({row, column})
    }

    applyFilter(value:string) {
        this.filterText = value
        this.datasource.filter = value.trim().toLowerCase();
    }

    clearFilter() {
        this.applyFilter('')
    }

    getAlignment(column:Column, header:boolean) {
        if (column.type === 'string' || column.type === 'date' || column.type === 'datetime') return header?"justify-items:left;":"text-align:left;"
        if (column.type === 'icon' || column.type === 'button' || column.type === 'icon-button') return header?"justify-items:center;":"text-align:center;"
        return header?"justify-items:right;":"text-align:right;"
    }

    // icon buttons and images have no text of their own, so they need a sortable, readable name
    isSortable(column:Column) {
        return column.type !== 'icon-button' && column.type !== 'node-svg' && column.type !== 'image'
    }
}
